import React, { useState } from 'react';
import { X, Share2, Check, ExternalLink, Globe, Coins } from 'lucide-react';

// ── Platform definitions ──────────────────────────────────────────
interface Platform {
  id: string;
  name: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  buildUrl: (text: string, url?: string) => string;
  charLimit: number;
  note?: string;
}

function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.737l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function BlueskyIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.204-.659-.299-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z" />
    </svg>
  );
}

function TelegramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function NostrIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
    </svg>
  );
}

const PLATFORMS: Platform[] = [
  {
    id: 'x',
    name: 'X (Twitter)',
    color: 'text-white',
    bgColor: 'bg-black',
    borderColor: 'border-black/20',
    icon: <XIcon size={15} />,
    charLimit: 280,
    buildUrl: (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
  {
    id: 'bluesky',
    name: 'Bluesky',
    color: 'text-white',
    bgColor: 'bg-[#0085ff]',
    borderColor: 'border-blue-500/20',
    icon: <BlueskyIcon size={15} />,
    charLimit: 300,
    buildUrl: (text) => `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}`,
  },
  {
    id: 'telegram',
    name: 'Telegram',
    color: 'text-white',
    bgColor: 'bg-[#2AABEE]',
    borderColor: 'border-sky-400/20',
    icon: <TelegramIcon size={15} />,
    charLimit: 4096,
    buildUrl: (text, url) =>
      url
        ? `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
        : `https://t.me/share/url?url=${encodeURIComponent('https://bchnostr.com')}&text=${encodeURIComponent(text)}`,
  },
];

// ── CrossPost modal ───────────────────────────────────────────────
interface CrossPostModalProps {
  /** The text content of the post */
  content: string;
  /** Public URL of the post (for sharing existing posts) */
  postUrl?: string;
  onClose: () => void;
}

// The BCH earn hook appended to every cross-post
const BCH_HOOK = `\n\n💚 Earn BCH tips for your posts → bchnostr.com`;
const PLAIN_SUFFIX = `\n\nbchnostr.com`;

export default function CrossPostModal({ content, postUrl, onClose }: CrossPostModalProps) {
  const [postedTo, setPostedTo] = useState<Set<string>>(new Set());
  const [customText, setCustomText] = useState(content);
  const [includeHook, setIncludeHook] = useState(true);

  // Append BCHNostr link + optional earn hook
  const buildShareText = (platform: Platform) => {
    let text = customText.trim();
    const suffix = includeHook
      ? (postUrl ? `\n\n${postUrl}${BCH_HOOK}` : BCH_HOOK)
      : (postUrl ? `\n\n${postUrl}` : PLAIN_SUFFIX);
    const maxLen = platform.charLimit - suffix.length;
    if (text.length > maxLen) {
      text = text.slice(0, maxLen - 3) + '...';
    }
    return text + suffix;
  };

  const handleShare = (platform: Platform) => {
    const text = buildShareText(platform);
    window.open(platform.buildUrl(text, postUrl), '_blank', 'noopener,noreferrer,width=600,height=500');
    setPostedTo(prev => new Set([...prev, platform.id]));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-primary" />
            <h2 className="font-bold text-sm">Cross-Post</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Editable preview */}
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">
              Edit your message
            </label>
            <textarea
              value={customText}
              onChange={e => setCustomText(e.target.value)}
              rows={4}
              className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
              placeholder="What do you want to say?"
            />
            {/* Earn BCH hook toggle */}
            <div className="mt-2 flex items-center justify-between rounded-xl bg-emerald-500/8 border border-emerald-500/20 px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <Coins className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Include "Earn BCH" hook</p>
                  <p className="text-[10px] text-muted-foreground truncate">Appends: 💚 Earn BCH tips → bchnostr.com</p>
                </div>
              </div>
              <button
                onClick={() => setIncludeHook(p => !p)}
                className={`shrink-0 ml-3 w-9 h-5 rounded-full transition-colors relative ${includeHook ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${includeHook ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {/* Platform buttons */}
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
              Share to
            </label>
            <div className="space-y-2">
              {PLATFORMS.map(platform => {
                const done = postedTo.has(platform.id);
                const shareText = buildShareText(platform);
                const charCount = shareText.length;
                const overLimit = charCount > platform.charLimit;

                return (
                  <div key={platform.id} className="flex items-center gap-3">
                    <button
                      onClick={() => handleShare(platform)}
                      disabled={overLimit}
                      className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                        done
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                          : overLimit
                            ? 'opacity-40 cursor-not-allowed border-border bg-muted'
                            : `${platform.bgColor} ${platform.color} ${platform.borderColor} hover:opacity-90 shadow-sm`
                      }`}
                    >
                      <span className="shrink-0">{done ? <Check className="w-4 h-4" /> : platform.icon}</span>
                      <span className="font-medium text-sm flex-1 text-left">{platform.name}</span>
                      {done ? (
                        <span className="text-[10px] font-semibold">Opened ✓</span>
                      ) : (
                        <span className={`text-[10px] font-mono ${overLimit ? 'text-red-500' : 'opacity-70'}`}>
                          {charCount}/{platform.charLimit}
                        </span>
                      )}
                      {!done && <ExternalLink className="w-3 h-3 opacity-70 shrink-0" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer note */}
          <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
            <Globe className="w-3 h-3 shrink-0" />
            Each platform opens in a new tab — you confirm before posting.
            Your followers can earn BCH tips by joining BCHNostr.
          </p>
        </div>
      </div>
    </div>
  );
}

